import type {
	ActionRegistration,
	ActionsConfig,
	HostActionDefinition,
} from "@getuserfeedback/protocol/host";

type LegacyCustomActionHandler = Extract<
	ActionRegistration,
	{ kind: "custom" }
>["handler"];
type GroupedCustomActionHandler = ActionsConfig["handler"];

type RegisteredCustomAction = {
	definition: HostActionDefinition;
	invoke: GroupedCustomActionHandler;
};

type OpenUrlActionHandler = Extract<
	ActionRegistration,
	{ kind: "open-url" }
>["handler"];

type ParsedRegistrations = {
	customActions: Map<string, RegisteredCustomAction>;
	openUrlHandler: OpenUrlActionHandler | undefined;
};

type UnknownRecord = Record<string, unknown>;

const isUnknownRecord = (value: unknown): value is UnknownRecord =>
	typeof value === "object" && value !== null;

const isLegacyCustomActionHandler = (
	value: unknown,
): value is LegacyCustomActionHandler => typeof value === "function";

const isGroupedCustomActionHandler = (
	value: unknown,
): value is GroupedCustomActionHandler => typeof value === "function";

const isOpenUrlActionHandler = (
	value: unknown,
): value is OpenUrlActionHandler => typeof value === "function";

const toIdentity = (definition: HostActionDefinition): string =>
	JSON.stringify([definition.key, definition.version]);

const compareText = (left: string, right: string): number =>
	left < right ? -1 : left > right ? 1 : 0;

const parseActionDefinition = (
	value: UnknownRecord,
	path: string,
): HostActionDefinition => {
	const key = value.key;
	const version = value.version;
	if (typeof key !== "string" || key.trim().length === 0) {
		throw new TypeError(`${path}.key must be a non-empty string`);
	}
	if (
		typeof version !== "number" ||
		!Number.isSafeInteger(version) ||
		version <= 0
	) {
		throw new TypeError(`${path}.version must be a positive safe integer`);
	}
	return { key: key.trim(), version };
};

const parseCustomRegistration = (
	value: UnknownRecord,
	index: number,
): RegisteredCustomAction => {
	const handler = value.handler;
	if (!isLegacyCustomActionHandler(handler)) {
		throw new TypeError(`actions[${index}].handler must be a function`);
	}
	return {
		definition: parseActionDefinition(value, `actions[${index}]`),
		invoke: (_requestedDefinition) => handler(),
	};
};

const parseGroupedConfiguration = (
	value: UnknownRecord,
): RegisteredCustomAction[] => {
	const definitions = value.definitions;
	const handler = value.handler;
	if (!Array.isArray(definitions)) {
		throw new TypeError("actions.definitions must be an array");
	}
	if (!isGroupedCustomActionHandler(handler)) {
		throw new TypeError("actions.handler must be a function");
	}
	return definitions.map((definition, index) => {
		if (typeof definition !== "object" || definition === null) {
			throw new TypeError(`actions.definitions[${index}] must be an object`);
		}
		return {
			definition: parseActionDefinition(
				definition,
				`actions.definitions[${index}]`,
			),
			invoke: handler,
		};
	});
};

const parseRegistrations = (
	registrations: ActionsConfig | ActionRegistration[] | undefined,
): ParsedRegistrations => {
	if (registrations !== undefined && !Array.isArray(registrations)) {
		if (!isUnknownRecord(registrations)) {
			throw new TypeError("actions must be an array or grouped configuration");
		}
		const customActions = new Map<string, RegisteredCustomAction>();
		for (const action of parseGroupedConfiguration(registrations)) {
			const identity = toIdentity(action.definition);
			if (customActions.has(identity)) {
				throw new TypeError(
					`actions contains duplicate custom definition "${action.definition.key}" at version ${action.definition.version}`,
				);
			}
			customActions.set(identity, action);
		}
		return { customActions, openUrlHandler: undefined };
	}
	const customActions = new Map<string, RegisteredCustomAction>();
	let openUrlHandler: ParsedRegistrations["openUrlHandler"];
	for (const [index, value] of (registrations ?? []).entries()) {
		if (!isUnknownRecord(value)) {
			throw new TypeError(`actions[${index}] must be an object`);
		}
		const kind = value.kind;
		if (kind === "custom") {
			const action = parseCustomRegistration(value, index);
			const identity = toIdentity(action.definition);
			if (customActions.has(identity)) {
				throw new TypeError(
					`actions contains duplicate custom definition "${action.definition.key}" at version ${action.definition.version}`,
				);
			}
			customActions.set(identity, action);
			continue;
		}
		if (kind === "open-url") {
			const handler = value.handler;
			if (!isOpenUrlActionHandler(handler)) {
				throw new TypeError(`actions[${index}].handler must be a function`);
			}
			if (openUrlHandler) {
				throw new TypeError(
					"actions can contain only one open-url registration",
				);
			}
			openUrlHandler = handler;
			continue;
		}
		throw new TypeError(
			`actions[${index}].kind must be "custom" or "open-url"`,
		);
	}
	return { customActions, openUrlHandler };
};

export type ActionRegistry = {
	getHandler: (
		definition: HostActionDefinition,
	) => LegacyCustomActionHandler | undefined;
	getOpenUrlHandler: () => OpenUrlActionHandler | undefined;
	listDefinitions: () => HostActionDefinition[];
	listRegistrationIdentities: () => string[];
	replace: (
		registrations: ActionsConfig | ActionRegistration[] | undefined,
	) => void;
};

export const createActionRegistry = (
	registrations: ActionsConfig | ActionRegistration[] | undefined,
): ActionRegistry => {
	let customActions = new Map<string, RegisteredCustomAction>();
	let openUrlHandler: ParsedRegistrations["openUrlHandler"];

	const replace = (
		next: ActionsConfig | ActionRegistration[] | undefined,
	): void => {
		const replacement = parseRegistrations(next);
		customActions = replacement.customActions;
		openUrlHandler = replacement.openUrlHandler;
	};

	replace(registrations);

	return {
		getHandler: (definition) => {
			const action = customActions.get(toIdentity(definition));
			if (!action) return undefined;
			const requestedDefinition = { ...action.definition };
			return () => action.invoke(requestedDefinition);
		},
		getOpenUrlHandler: () => openUrlHandler,
		listDefinitions: () =>
			[...customActions.values()]
				.map(({ definition }) => ({ ...definition }))
				.sort(
					(left, right) =>
						compareText(left.key, right.key) || left.version - right.version,
				),
		listRegistrationIdentities: () => [
			...[...customActions.values()]
				.map(({ definition }) => `custom:${toIdentity(definition)}`)
				.sort(compareText),
			...(openUrlHandler ? ["open-url"] : []),
		],
		replace,
	};
};
